import React from 'react';
import CustomContainer from "../Components/CustomContainer";
import {generatePath, useParams} from "react-router-dom";
import ConstructionSiteAction from "../../Actions/ConstructionSiteAction";
import Project from "../Home/Components/Project";
import Loader from "../Components/Loader";
import RiskAnalysisAction from "../../Actions/RiskAnalysisAction";
import {Breadcrumb, Button, Modal} from "react-bootstrap";
import CustomTranslation from "../Components/CustomTranslation";
import CustomBorderedButton from "../Components/CustomBorderedButton";
import G from "../../Actions/GeneralAction";
import CustomInput from "../Components/CustomInput";
import TranslationAction from "../../Actions/TranslationAction";
import Uuid from 'uuid/v1';
import UserAction from "../../Actions/UserAction";
import RiskAnalysisDocumentSection from "./Components/RiskAnalysisDocumentSection";
import RequestTranslationModal from "./Components/RequestTranslationModal";
import TranslationRequestAction from "../../Actions/TranslationRequestAction";
import LanguageAction from "../../Actions/LanguageAction";
import {ENABLE_RA_TRANSLATION} from "../../Config/FeatureFlags";
import fileDownload from "js-file-download";
import Config from "../../Config.json";
import {toast} from "react-hot-toast";

const MAX_TRANSLATIONS_PER_RA = 3;

//define class
class RiskAnalysisScreen extends React.Component {

    //constructor
    constructor(props) {
        super(props);

        //state
        this.state = {
            modalVisible: false,
            translationRequestModalVisible: false,
            translations: [],
            translationRequests: [],
            allowedDeepLLanguages: [],
            fullDownloading: false,
            limitedDownloading: false,
            renderId: Uuid()
        }
    }

    //on mount
    componentDidMount = () => {
        this.updateElements();
    }

    //get data
    updateElements = async () => {

        //get risk analysis
        let ra = await RiskAnalysisAction.get(this.props.params.id);

        //get project
        let cs = await ConstructionSiteAction.getConstructionSite(ra.constructionSite.id);

        //translate title
        let title = await TranslationAction.translateArray(ra.title);

        //get rights
        let userConstructionSiteRights = await UserAction.getUserRightByConstructionSiteId(cs.id);
        let userCompanyRights = await UserAction.getUserRightByCompanyId(cs.company.id);
        if (!userConstructionSiteRights && userCompanyRights && userCompanyRights.adminRights) {
            userConstructionSiteRights = { managementRights: true};
        }
        if (!userConstructionSiteRights) {
            userConstructionSiteRights = { managementRights: false };
        }

        //fetch translations/requests for this RA + allowed languages, in parallel
        let [translations, translationRequests, allowedDeepLLanguages] = await Promise.all([
            TranslationRequestAction.getTranslations(ra.id),
            TranslationRequestAction.getTranslationRequests(ra.id),
            LanguageAction.getAllowedDeepLLanguages()
        ]);

        //set state
        this.setState({
            data: {
                constructionSite: cs,
                riskAnalysis: ra,
                rights: userConstructionSiteRights
            },
            title: title,
            id: ra.id,
            translations: translations || [],
            translationRequests: translationRequests || [],
            allowedDeepLLanguages: allowedDeepLLanguages || []
        });
    }

    //safe accessors — mirrors the RN screen's helpers
    getTranslations = () => {
        return this.state.translations || [];
    }

    getPendingTranslationRequests = () => {
        return (this.state.translationRequests || []).filter((r) => r.status === 'pending');
    }

    //combine accepted translations + pending requests into the list RiskAnalysisDocumentSection expects
    buildTranslationsList = (limited) => {
        const accepted = this.getTranslations().map((t) => {
            const documentKey = (limited ? 'limited' : 'full') + '-' + t.language;
            return {
                label: LanguageAction.getLanguageDisplayName(t.language, this.state.allowedDeepLLanguages),
                status: 'ready',
                onDownload: !this.state[documentKey + 'Downloading']
                    ? () => this.downloadFile(
                        Config.BASE_URL + '/en/general/document/tra/translation/' + t.id + '/' + (limited ? '1' : '0') + '/1',
                        documentKey
                    )
                    : null
            };
        });

        const pending = this.getPendingTranslationRequests().map((r) => ({
            label: LanguageAction.getLanguageDisplayName(r.language, this.state.allowedDeepLLanguages),
            status: 'pending'
        }));

        return [...accepted, ...pending];
    }

    //open the request-translation modal
    onRequestTranslation = () => {
        this.setState({translationRequestModalVisible: true});
    }

    //submit a translation request for the chosen language
    requestTranslation = async (languageCode) => {
        let result = await TranslationRequestAction.requestTranslation(this.state.data.riskAnalysis.id, languageCode);
        this.setState({translationRequestModalVisible: false});
        if (result && result.id) {
            this.setState({
                translationRequests: [...(this.state.translationRequests || []), result]
            });
        }
    }

    //download a document, tracking a busy flag per document key so the button can disable/show progress
    downloadFile = (url, documentKey) => {
        let downloadingState = {};
        downloadingState[documentKey + 'Downloading'] = true;
        this.setState(downloadingState);

        //default file name
        let fileName = 'document.pdf';
        fetch(url)
            .then(res => {
                fileName = res.headers.get('content-disposition');
                let parts = fileName.split(';');
                fileName = parts[1].split('=')[1];
                return res.blob();
            })
            .then(res => {
                fileName = fileName.replace(/['"]+/g, '');
                fileDownload(res, fileName, 'application/pdf');
            })
            .catch(async () => {
                let toastMessage = await TranslationAction.translate('KEY.Download failed');
                toast.error(toastMessage);
            })
            .finally(() => {
                let doneState = {};
                doneState[documentKey + 'Downloading'] = false;
                this.setState(doneState);
            });
    }

    //set new title
    updateTitle = async () => {

        //get current language
        let user = await UserAction.getCurrentUser();
        let language = user.uiLanguage;

        //get result
        let result = await RiskAnalysisAction.update(this.state, language);

        //set in data
        this.setState({
            data: {...this.state.data, ...{riskAnalysis: result}},
            modalVisible: false,
            renderId: Uuid()
        });
    }

    //render view
    render() {

        if (this.state.data) {
            return (
                <CustomContainer>
                    <div key={this.state.renderId}>
                        {/* Project header */}
                        <div style={{position: 'sticky', top: '2rem', zIndex: 9}}>
                            <Project
                                float
                                data={this.state.data.constructionSite}
                            />
                        </div>

                        {/* Breadcrumb */}
                        <Breadcrumb className={'vertical-margin-2'}>
                            <Breadcrumb.Item
                                href={generatePath('/')}
                            >
                                <CustomTranslation value={'KEY.Home'} />
                            </Breadcrumb.Item>
                            <Breadcrumb.Item
                                href={generatePath('/construction_site/:id', {id: this.state.data.constructionSite.id})}
                            >
                                {this.state.data.constructionSite.name}
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active>
                                <CustomTranslation value={this.state.data.riskAnalysis.title} />
                            </Breadcrumb.Item>
                        </Breadcrumb>

                        {/* Title */}
                        <div>
                            <h1>
                                <CustomTranslation value={'KEY.Detail Risk Analysis'}/>
                            </h1>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'wrap',
                                marginLeft: '-3px',
                                marginRight: '-3px'
                            }}
                        >
                            {/* Change title */}
                            {this.state.data.rights.managementRights &&
                                <CustomBorderedButton
                                    className={'standard-margin-3 vertical-margin-3'}
                                    style={{flex: 1, flexBasis: 250}}
                                    title={<CustomTranslation value={'KEY.Edit title'}/>}
                                    icon={'las la-edit'}
                                    onClick={() => this.setState({modalVisible: true})}
                                />
                            }

                            {/* Review translation requests */}
                            {ENABLE_RA_TRANSLATION && this.state.data.rights.managementRights && this.getPendingTranslationRequests().length > 0 &&
                                <CustomBorderedButton
                                    className={'standard-margin-3 vertical-margin-3'}
                                    style={{flex: 1, flexBasis: 250}}
                                    title={<><CustomTranslation value={'KEY.Review translation requests'}/> ({this.getPendingTranslationRequests().length})</>}
                                    icon={'las la-language'}
                                    onClick={() => {
                                        window.location.href = generatePath('/risk_analysis/:id/translation_requests', {id: this.state.data.riskAnalysis.id});
                                    }}
                                />
                            }

                            {/* Risk analysis edit */}
                            {this.state.data.rights.managementRights &&
                                <div
                                    className={'rounded standard-padding vertical-padding touchable standard-margin-3 vertical-margin-3 ' + (
                                        this.state.data.riskAnalysis.isFinished ? 'bg-success' : 'bg-primary'
                                    )}
                                    style={{
                                        flex: 1,
                                        flexBasis: 250,
                                        display: 'flex',
                                        flexDirection: 'row'
                                    }}
                                    onClick={() => {
                                        window.location.href = generatePath('/risk_analysis/:id/edit', {id: this.state.data.riskAnalysis.id});
                                    }}
                                >
                                    <div>
                                        <div
                                            className={'bg-white'}
                                            style={{
                                                height: '1.5rem',
                                                width: '1.5rem',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                flexDirection: 'row',
                                                justifyContent: 'center',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <i
                                                className={'las la-cog ' + (
                                                    this.state.data.riskAnalysis.isFinished ? 'text-success' : 'text-primary'
                                                )}
                                                style={{
                                                    fontSize: '1rem'
                                                }}
                                            >
                                            </i>
                                        </div>
                                    </div>
                                    <div
                                        className={'standard-margin-2'}
                                        style={{
                                            marginRight: 0
                                        }}
                                    >
                                        <h3 className={'text-white'}>
                                            <CustomTranslation value={this.state.data.riskAnalysis.title}/>
                                        </h3>
                                        <h5 className={'unbold text-white'}>
                                            {
                                                (this.state.data.riskAnalysis.startedBy.firstName || this.state.data.riskAnalysis.startedBy.lastName)
                                                    ?
                                                    (this.state.data.riskAnalysis.startedBy.firstName || '') + ' ' + (this.state.data.riskAnalysis.startedBy.lastName || '')
                                                    :
                                                    (this.state.data.riskAnalysis.startedBy.email || '')
                                            } [{G.formatDate(this.state.data.riskAnalysis.createdAt)}]
                                        </h5>
                                    </div>
                                </div>
                            }
                        </div>

                        {this.state.data.riskAnalysis.isFinished &&
                            <div>
                                {/* Full risk analysis */}
                                <RiskAnalysisDocumentSection
                                    style={{display: 'block'}}
                                    className={'standard-margin-3 vertical-margin-3'}
                                    title={<CustomTranslation value={'KEY.Full risk analysis'}/>}
                                    onDownloadOriginal={!this.state.fullDownloading ? () => this.downloadFile(Config.BASE_URL + '/en/general/document/tra/full/' + this.state.data.riskAnalysis.id + '/1', 'full') : null}
                                    onMailPress={(this.state.data.rights.managementRights) ? () => {
                                        window.location.href = generatePath('/risk_analysis/:id/email/:limited', {id: this.state.data.riskAnalysis.id, limited: 0});
                                    } : null}
                                    progress={this.state.fullDownloading}
                                    translations={this.buildTranslationsList(false)}
                                    onRequestTranslation={this.onRequestTranslation}
                                    disableRequestTranslation={(this.getTranslations().length + this.getPendingTranslationRequests().length) >= MAX_TRANSLATIONS_PER_RA}
                                    originalLanguageLabel={LanguageAction.getLanguageDisplayName('NL', this.state.allowedDeepLLanguages)}
                                    showTranslations={ENABLE_RA_TRANSLATION}
                                />

                                {/* Limited risk analysis */}
                                <RiskAnalysisDocumentSection
                                    style={{display: 'block'}}
                                    className={'standard-margin-3 vertical-margin-3'}
                                    title={<CustomTranslation value={'KEY.Limited risk analysis'}/>}
                                    onDownloadOriginal={!this.state.limitedDownloading ? () => this.downloadFile(Config.BASE_URL + '/en/general/document/tra/limited/' + this.state.data.riskAnalysis.id + '/1', 'limited') : null}
                                    onMailPress={(this.state.data.rights.managementRights) ? () => {
                                        window.location.href = generatePath('/risk_analysis/:id/email/:limited', {id: this.state.data.riskAnalysis.id, limited: 1});
                                    } : null}
                                    progress={this.state.limitedDownloading}
                                    translations={this.buildTranslationsList(true)}
                                    onRequestTranslation={this.onRequestTranslation}
                                    disableRequestTranslation={(this.getTranslations().length + this.getPendingTranslationRequests().length) >= MAX_TRANSLATIONS_PER_RA}
                                    originalLanguageLabel={LanguageAction.getLanguageDisplayName('NL', this.state.allowedDeepLLanguages)}
                                    showTranslations={ENABLE_RA_TRANSLATION}
                                />
                            </div>
                        }

                        {/* Title change modal */}
                        <Modal
                            show={this.state.modalVisible}
                            onHide={null}
                            backdrop={'static'}
                            keyboard={false}
                        >
                            <Modal.Body>
                                <div>
                                    <CustomInput
                                        label={<CustomTranslation value={'KEY.Title'} />}
                                        iconName={'las la-quote-left'}
                                        value={this.state.title}
                                        onChangeText={(text) => this.setState({title: text})}
                                    />
                                </div>
                            </Modal.Body>
                            <Modal.Footer>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        width: '100%'
                                    }}
                                >
                                    <div className={'standard-margin-3'} style={{flex: 1, marginLeft: 0}}>
                                        <Button
                                            block
                                            variant={'dark'}
                                            onClick={() => this.setState({modalVisible: false})}
                                        >
                                            <CustomTranslation value={'KEY.Cancel'} />
                                        </Button>
                                    </div>
                                    <div className={'standard-margin-3'} style={{flex: 1, marginRight: 0}}>
                                        <Button
                                            block
                                            variant={'success'}
                                            onClick={() => this.updateTitle()}
                                        >
                                            <CustomTranslation value={'KEY.Save'} />
                                        </Button>
                                    </div>
                                </div>
                            </Modal.Footer>
                        </Modal>

                        {/* Request translation modal */}
                        {ENABLE_RA_TRANSLATION &&
                            <RequestTranslationModal
                                visible={this.state.translationRequestModalVisible}
                                languages={this.state.allowedDeepLLanguages || []}
                                excludeLanguageCodes={[
                                    ...this.getTranslations().map((t) => t.language),
                                    ...this.getPendingTranslationRequests().map((r) => r.language)
                                ]}
                                onClose={() => this.setState({translationRequestModalVisible: false})}
                                onSubmit={(codes) => this.requestTranslation(codes[0])}
                            />
                        }
                    </div>
                </CustomContainer>
            );
        } else {
            return (
                <CustomContainer>
                    <Loader />
                </CustomContainer>
            );
        }
    }
}

//export
export default function (props) {
    const params = useParams();
    return <RiskAnalysisScreen {...props} params={params} />;
}
